'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/lib/config/useLanguage';
import { buildLeadThankYouUrl, clearPendingLeadSubmissionId, extractOptionalLeadId, getOrCreatePendingLeadSubmissionId } from '@/lib/forms/lead-thank-you';
import { useInvisibleSmartCaptcha } from '@/components/SmartCaptcha';
import { EMAIL_INPUT_PATTERN, formatRussianPhone, RUSSIAN_PHONE_PATTERN } from '@/lib/forms/contact-validation';

const FORM_TYPE = 'contact';

const Form = () => {
    const { localizePath } = useLanguage();
    const router = useRouter();
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        comment: '',
        privacy: false,
        marketing: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const { captchaElement, executeCaptcha, resetCaptcha } = useInvisibleSmartCaptcha();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'phone' ? formatRussianPhone(value) : value
        }));
    };

    const handleCheckboxChange = (name, checked) => {
        setFormData(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Проверяем согласие на обработку персональных данных
        if (!formData.privacy) {
            setSubmitMessage('Необходимо дать согласие на обработку персональных данных');
            return;
        }

        setIsSubmitting(true);
        setSubmitMessage('');

        try {
            const submissionId = getOrCreatePendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
            const smartToken = await executeCaptcha();
            const response = await fetch('/api/forms', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name,
                    phone: formData.phone,
                    email: formData.email,
                    message: formData.comment,
                    privacy: formData.privacy,
                    marketing: formData.marketing,
                    formType: FORM_TYPE,
                    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
                    smartToken,
                    submissionId,
                })
            });

            console.log('Response:', response);

            const result = await response.json();
            console.log('Result:', result);

            if (response.ok && result.success) {
                const thankYouUrl = buildLeadThankYouUrl({
                    pathname: window.location.pathname,
                    search: window.location.search,
                    formType: FORM_TYPE,
                    leadId: extractOptionalLeadId(result),
                    sid: submissionId,
                });

                clearPendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
                router.push(thankYouUrl);
                return;
            } else {
                setSubmitMessage(result.message || 'Ошибка при отправке заявки. Попробуйте еще раз.');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitMessage('Ошибка при отправке заявки. Попробуйте еще раз.');
        } finally {
            resetCaptcha();
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* ФИО поле */}
            <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ФИО <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    autoComplete="name"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="Иванов Иван Иванович"
                />
            </div>

            {/* Телефон поле */}
            <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Телефон <span className="text-red-500">*</span>
                </label>
                <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    inputMode="tel"
                    autoComplete="tel"
                    maxLength={18}
                    pattern={RUSSIAN_PHONE_PATTERN}
                    title="Введите телефон в формате +7 (999) 123-45-67"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="+7 (999) 123-45-67"
                />
            </div>

            {/* Email поле */}
            <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email адрес <span className="text-red-500">*</span>
                </label>
                <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    inputMode="email"
                    autoComplete="email"
                    pattern={EMAIL_INPUT_PATTERN}
                    title="Введите корректный email, например name@example.ru"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="Введите ваш email"
                />
            </div>

            {/* Сообщение поле */}
            <div>
                <label htmlFor="comment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Сообщение
                </label>
                <textarea
                    id="comment"
                    name="comment"
                    value={formData.comment}
                    onChange={handleInputChange}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white resize-none transition-colors"
                    placeholder="Расскажите о вашем проекте или задайте вопрос..."
                />
            </div>

            {/* Чекбоксы согласия */}
            <div className="space-y-3">
                <div className="flex items-start">
                    <Checkbox
                        id="privacy"
                        checked={formData.privacy}
                        onCheckedChange={(checked) => handleCheckboxChange('privacy', checked)}
                        required
                        className="mt-1"
                    />
                    <Label htmlFor="privacy" className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                        Я даю согласие на обработку своих персональных данных и соглашаюсь с{' '}
                        <a href={localizePath('/pages/politika-konfidentsialnosti-personalnykh-dannykh')} className="text-blue-600 hover:underline dark:text-blue-400">
                            политикой конфиденциальности
                        </a>
                        . <span className="text-red-500">*</span>
                    </Label>
                </div>

                <div className="flex items-start">
                    <Checkbox
                        id="marketing"
                        checked={formData.marketing}
                        onCheckedChange={(checked) => handleCheckboxChange('marketing', checked)}
                        className="mt-1"
                    />
                    <Label htmlFor="marketing" className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                        Я даю согласие на направление рекламных рассылок.
                    </Label>
                </div>
            </div>

            {captchaElement}

            {/* Кнопка отправки */}
            <div>
                <button
                    type="submit"
                    disabled={isSubmitting || !formData.privacy}
                    className={`w-full py-3 px-6 rounded-lg font-medium text-white transition-all duration-200 ${
                        isSubmitting || !formData.privacy
                            ? 'bg-gray-400 cursor-not-allowed opacity-50'
                            : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800'
                    }`}
                >
                    {isSubmitting ? (
                        <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Отправка...
                        </span>
                    ) : (
                        'Отправить сообщение'
                    )}
                </button>
            </div>

            {/* Сообщение о результате */}
            {submitMessage && (
                <div className={`p-4 rounded-lg border ${
                    submitMessage.includes('успешно')
                        ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400'
                        : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
                }`}>
                    <div className="flex">
                        <div className="flex-shrink-0">
                            {submitMessage.includes('успешно') ? (
                                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            ) : (
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            )}
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium">
                                {submitMessage}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </form>
    );
};

export default Form;
